import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useABTest, MODAL_AB_TEST_CONFIG, DECLINE_TEXT_BY_BRAND } from '@/hooks/useABTest';
import { Confetti } from '@/components/modals/Confetti';
import { triggerCelebration } from '@/lib/celebrationEffects';
import { cn } from '@/lib/utils';
import {
  PremiumModalShell,
  premiumInputStyles,
  getBrandFocusRing,
  getPrimaryButtonStyles,
} from '@/components/modals/PremiumModalShell';

type ModalStep = 'email' | 'phone' | 'success';

const STORAGE_KEY = 'bbq_savings_modal_dismissed';
const SUBMISSION_KEY = 'bbq_savings_modal_submitted';
const DISMISS_DAYS = 7;
const SUBMISSION_DAYS = 60;

const generateSavingsCode = (): string => {
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `BBQ60-${random}`;
};

// Format phone to E.164
const formatPhoneE164 = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  return `+${cleaned}`;
};

// Format phone for display
const formatPhoneDisplay = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

export const BBQSavingsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ModalStep>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingsCode, setSavingsCode] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  
  // SMS Consent state
  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  const { variant, trackConversion } = useABTest(MODAL_AB_TEST_CONFIG);
  const content = variant.value;

  useEffect(() => {
    const submittedAt = localStorage.getItem(SUBMISSION_KEY);
    if (submittedAt) {
      const daysSinceSubmission = (Date.now() - parseInt(submittedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceSubmission < SUBMISSION_DAYS) return;
    }

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < DISMISS_DAYS) return;
    }

    const delay = 8000 + Math.random() * 4000;
    const timer = setTimeout(() => setIsOpen(true), delay);

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        setIsOpen(true);
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
    
    if (window.innerWidth > 768) {
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setIsOpen(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast({ title: "Please enter a valid email", variant: "destructive" });
      return;
    }
    setStep('phone');
  };

  const triggerSuccess = (code: string) => {
    setSavingsCode(code);
    localStorage.setItem(SUBMISSION_KEY, Date.now().toString());
    setStep('success');
    setShowConfetti(true);
    triggerCelebration('bbq');
    trackConversion();
  };

  // Create or update SMS contact and record consent
  const recordSMSConsent = async (phoneE164: string, phoneDisplay: string) => {
    try {
      // Create or update contact
      const { data: existingContact } = await supabase
        .from('sms_contacts')
        .select('id')
        .eq('phone_e164', phoneE164)
        .single();

      let contactId: string;

      if (existingContact) {
        contactId = existingContact.id;
        // Update existing contact
        await supabase
          .from('sms_contacts')
          .update({
            email: email.trim(),
            transactional_consent: transactionalConsent || undefined,
            marketing_consent: marketingConsent || undefined,
            status: 'active',
            updated_at: new Date().toISOString(),
          })
          .eq('id', contactId);
      } else {
        // Create new contact
        const { data: newContact, error: contactError } = await supabase
          .from('sms_contacts')
          .insert({
            phone_e164: phoneE164,
            phone_display: phoneDisplay,
            email: email.trim(),
            transactional_consent: transactionalConsent,
            marketing_consent: marketingConsent,
            status: 'active',
          })
          .select('id')
          .single();

        if (contactError) throw contactError;
        contactId = newContact.id;
      }

      // Record transactional consent if given
      if (transactionalConsent) {
        await supabase.from('sms_consent_records').insert({
          contact_id: contactId,
          phone_e164: phoneE164,
          consent_type: 'transactional',
          consent_granted: true,
          consent_source: 'web_form',
          source_page: window.location.pathname,
          consent_text: 'I agree to receive order updates and service messages via SMS.',
          consent_language_version: '1.0',
        });
      }

      // Record marketing consent if given
      if (marketingConsent) {
        await supabase.from('sms_consent_records').insert({
          contact_id: contactId,
          phone_e164: phoneE164,
          consent_type: 'marketing',
          consent_granted: true,
          consent_source: 'web_form',
          source_page: window.location.pathname,
          consent_text: 'I agree to receive promotional offers and updates via SMS. Message frequency varies. Reply STOP to unsubscribe.',
          consent_language_version: '1.0',
        });
      }

      // Log the consent event
      await supabase.from('sms_audit_log').insert({
        event_type: 'consent_given',
        contact_id: contactId,
        phone_e164: phoneE164,
        actor_type: 'user',
        details: {
          source: 'savings_modal',
          transactional_consent: transactionalConsent,
          marketing_consent: marketingConsent,
          email: email.trim(),
        },
      });

    } catch (error) {
      console.error('Error recording SMS consent:', error);
      // Don't block the main flow if consent recording fails
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone if consent is given
    if ((transactionalConsent || marketingConsent) && !phone.trim()) {
      toast({ 
        title: "Phone number required", 
        description: "Please enter your phone number to receive SMS messages.",
        variant: "destructive" 
      });
      return;
    }

    setIsSubmitting(true);

    const code = generateSavingsCode();

    try {
      const { error } = await supabase.from('leads').insert({
        email: email.trim(),
        phone: phone.trim() || null,
        site_key: 'bbq',
        source_page: window.location.pathname,
        savings_code: code,
      });

      if (error) throw error;

      // Record SMS consent if phone provided and consent given
      if (phone.trim() && (transactionalConsent || marketingConsent)) {
        const phoneE164 = formatPhoneE164(phone);
        const phoneDisplay = formatPhoneDisplay(phone);
        await recordSMSConsent(phoneE164, phoneDisplay);
      }

      triggerSuccess(code);
      toast({
        title: "You're fired up!",
        description: "Your savings code is ready!",
      });
    } catch (error) {
      console.error('Lead submission error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipPhone = async () => {
    setIsSubmitting(true);
    const code = generateSavingsCode();

    try {
      const { error } = await supabase.from('leads').insert({
        email: email.trim(),
        site_key: 'bbq',
        source_page: window.location.pathname,
        savings_code: code,
      });

      if (error) throw error;
      triggerSuccess(code);
    } catch (error) {
      console.error('Lead submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = cn(premiumInputStyles, getBrandFocusRing('bbq'));

  return (
    <>
      <Confetti isActive={showConfetti} colors={['#f97316', '#fbbf24', '#ea580c', '#fb923c', '#f59e0b']} />
      <PremiumModalShell
        isOpen={isOpen}
        onClose={handleDismiss}
        brand="bbq"
        maxWidth="2xl"
        imageUrl="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80"
        imageAlt="Outdoor kitchen with grills"
      >
        <AnimatePresence mode="wait">
          {step === 'email' && (
            <motion.div
              key="email"
              initial={shouldReduceMotion ? {} : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={shouldReduceMotion ? {} : { opacity: 0 }}
              className="flex flex-col justify-center min-h-[400px]"
            >
              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-medium text-neutral-800 tracking-tight leading-tight">
                  {content.headline}
                </h2>
                <p className="mt-3 text-neutral-500 leading-relaxed">
                  {content.subheadline}
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClasses}
                  required
                />
                
                <Button 
                  type="submit" 
                  className={cn(getPrimaryButtonStyles('bbq'), "group")}
                >
                  {content.ctaText}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </form>

              <button
                onClick={handleDismiss}
                className="mt-6 text-sm text-neutral-400 hover:text-neutral-600 transition-colors text-center"
              >
                {DECLINE_TEXT_BY_BRAND.bbq}
              </button>

              <div className="mt-8 pt-6 border-t border-neutral-100 flex items-center justify-center gap-6 text-neutral-400 text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Showroom exclusive
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  No spam
                </span>
              </div>
            </motion.div>
          )}

          {step === 'phone' && (
            <motion.div
              key="phone"
              initial={shouldReduceMotion ? {} : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={shouldReduceMotion ? {} : { opacity: 0 }}
              className="flex flex-col justify-center min-h-[400px]"
            >
              <div className="mb-6">
                <h2 className="text-2xl sm:text-3xl font-medium text-neutral-800 tracking-tight">
                  Where should we text your code?
                </h2>
                <p className="text-neutral-500 mt-3 leading-relaxed">
                  Get your exclusive savings code sent straight to your phone.
                </p>
              </div>

              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                <Input
                  type="tel"
                  placeholder="Your mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClasses}
                  required={transactionalConsent}
                />
                
                {/* SMS Consent Checkboxes - TCPA Compliant */}
                <div className="space-y-3 py-1">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <Checkbox
                      id="transactional-consent"
                      checked={transactionalConsent}
                      onCheckedChange={(checked) => setTransactionalConsent(checked === true)}
                      className="mt-0.5 border-neutral-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <span className="text-xs text-neutral-600 leading-relaxed group-hover:text-neutral-800 transition-colors">
                      I agree to receive text messages about my inquiry (transactional). <span className="text-orange-500">*</span>
                    </span>
                  </label>
                  
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <Checkbox
                      id="marketing-consent"
                      checked={marketingConsent}
                      onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                      className="mt-0.5 border-neutral-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <span className="text-xs text-neutral-600 leading-relaxed group-hover:text-neutral-800 transition-colors">
                      Send me promotions and special offers by text.
                    </span>
                  </label>
                </div>

                {/* TCPA Compliance Disclosure */}
                <p className="text-[10px] text-neutral-400 leading-relaxed">
                  Message & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.
                </p>
                
                <Button 
                  type="submit" 
                  className={getPrimaryButtonStyles('bbq')}
                  disabled={isSubmitting || !transactionalConsent || !phone.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Text Me My Code'}
                </Button>

                {/* Privacy/Terms Links */}
                <p className="text-[10px] text-neutral-400 text-center leading-relaxed">
                  By continuing, you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-600">Privacy Policy</a> and{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-600">Terms of Service</a>.
                </p>
              </form>

              <button
                onClick={handleSkipPhone}
                disabled={isSubmitting}
                className="mt-4 text-sm text-neutral-400 hover:text-neutral-600 transition-colors text-center"
              >
                Skip — just email me
              </button>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div
              key="success"
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center text-center min-h-[400px]"
            >
              <motion.div 
                initial={shouldReduceMotion ? {} : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
                className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-6"
              >
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </motion.div>

              <h2 className="text-2xl sm:text-3xl font-medium text-neutral-800 mb-2">
                🔥 You're Fired Up!
              </h2>
              <p className="text-neutral-500 mb-6">
                Your exclusive showroom savings code is ready.
              </p>

              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 text-center w-full max-w-sm">
                <p className="text-sm text-neutral-500 mb-2">Your savings code:</p>
                <p className="text-3xl font-mono font-semibold text-orange-500 tracking-wider">
                  {savingsCode}
                </p>
                <p className="text-xs text-neutral-400 mt-3">
                  Show this code in-store or mention it when requesting your quote
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm mt-6">
                <Button 
                  asChild 
                  variant="outline" 
                  className="flex-1 h-12 border-neutral-200 text-neutral-600 hover:bg-neutral-50 rounded-lg"
                  onClick={() => setIsOpen(false)}
                >
                  <a href="/showroom">Visit Showroom</a>
                </Button>
                <Button 
                  className={cn(getPrimaryButtonStyles('bbq'), "flex-1")}
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      try {
                        const event = new CustomEvent('openQuoteModal', { detail: { buttonLabel: 'Get Quote (Savings Modal)' } });
                        window.dispatchEvent(event);
                      } catch (e) {
                        window.location.href = '/quote';
                      }
                    }, 300);
                  }}
                >
                  Get Quote
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </PremiumModalShell>
    </>
  );
};

export default BBQSavingsModal;
